/**
 * New Project Modal Component
 * Modal for creating a new project (ZIP upload or manual)
 */

import { useState, useRef, useEffect } from 'react';
import { X, Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { projectsApi } from '../../lib/api';

// Auto-closing success component
function SuccessStep({ onComplete }: { onComplete: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400" />
      </div>
      <h3 className="text-lg font-mono font-bold text-slate-800 dark:text-slate-100 mb-2 tracking-wide">
        PROJECT CREATED
      </h3>
      <p className="text-slate-500 dark:text-slate-400 font-mono text-sm">
        The project is ready to use.
      </p>
    </div>
  );
}

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (projectId: string) => void;
}

type Step = 'choose' | 'upload' | 'manual' | 'loading' | 'success' | 'error';
type LoadingStage = 'uploading' | 'extracting' | 'processing' | 'storage' | 'finalizing';

const loadingStages: { id: LoadingStage; label: string; description: string }[] = [
  { id: 'uploading', label: 'UPLOADING', description: 'Sending file to server...' },
  { id: 'extracting', label: 'EXTRACTING', description: 'Unpacking archive...' },
  { id: 'processing', label: 'PROCESSING', description: 'Analyzing photos and data...' },
  { id: 'storage', label: 'STORAGE', description: 'Uploading to cloud storage...' },
  { id: 'finalizing', label: 'FINALIZING', description: 'Creating project...' },
];

export function NewProjectModal({ isOpen, onClose, onProjectCreated }: NewProjectModalProps) {
  const [step, setStep] = useState<Step>('choose');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('uploading');
  const [error, setError] = useState<string | null>(null);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manual form state
  const [manualForm, setManualForm] = useState({
    code: '',
    name: '',
    line: '',
    surveyDate: '',
    epsg: '32632',
  });

  const resetModal = () => {
    setStep('choose');
    setLoadingProgress(0);
    setLoadingMessage('');
    setLoadingStage('uploading');
    setError(null);
    setCreatedProjectId(null);
    setManualForm({
      code: '',
      name: '',
      line: '',
      surveyDate: '',
      epsg: '32632',
    });
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleZipUpload = async (file: File) => {
    setStep('loading');
    setLoadingProgress(0);
    setLoadingStage('uploading');
    setLoadingMessage('Sending file to server...');
    setError(null);

    try {
      // Stage 1: Upload file to server
      let uploadComplete = false;
      
      const response = await projectsApi.importZip(file, (progressEvent) => {
        const percentCompleted = progressEvent.total 
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0;
        setLoadingProgress(percentCompleted);
        
        if (percentCompleted >= 100 && !uploadComplete) {
          uploadComplete = true;
          // File uploaded, now server is processing
          setLoadingStage('extracting');
          setLoadingMessage('Unpacking archive...');
          
          // Simulate stages while waiting for server response
          setTimeout(() => {
            setLoadingStage('processing');
            setLoadingMessage('Analyzing photos and data...');
          }, 1500);
          
          setTimeout(() => {
            setLoadingStage('storage');
            setLoadingMessage('Uploading to cloud storage...');
          }, 3000);
          
          setTimeout(() => {
            setLoadingStage('finalizing');
            setLoadingMessage('Creating project...');
          }, 5000);
        }
      });

      const project = response.data.data;
      setCreatedProjectId(project.id);
      setStep('success');
    } catch (err: any) {
      console.error('Failed to load ZIP:', err);
      setError(err.response?.data?.message || err.message || 'Error loading file');
      setStep('error');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleZipUpload(file);
    }
  };

  const handleManualCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!manualForm.code || !manualForm.name) {
      setError('Code and name are required');
      return;
    }

    try {
      const response = await projectsApi.create({
        code: manualForm.code,
        name: manualForm.name,
        line: manualForm.line || undefined,
        surveyDate: manualForm.surveyDate || undefined,
        epsg: parseInt(manualForm.epsg),
      });

      const project = response.data.data;
      setCreatedProjectId(project.id);
      setStep('success');
    } catch (err: any) {
      console.error('Failed to create project:', err);
      setError(err.response?.data?.message || err.message || 'Error creating project');
      setStep('error');
    }
  };

  const handleSuccess = () => {
    // Dispatch custom event to notify all components
    window.dispatchEvent(new CustomEvent('projects-updated'));
    if (createdProjectId) {
      onProjectCreated(createdProjectId);
    }
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/50 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700/50">
          <h2 className="text-lg font-mono font-bold text-slate-800 dark:text-slate-100 tracking-wide">
            {step === 'choose' && 'NEW PROJECT'}
            {step === 'upload' && 'UPLOAD ZIP'}
            {step === 'manual' && 'PROJECT DETAILS'}
            {step === 'loading' && 'PROCESSING...'}
            {step === 'success' && 'PROJECT CREATED'}
            {step === 'error' && 'ERROR'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Choose Step */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-slate-500 dark:text-slate-400 mb-6 font-mono text-sm">Select project creation method:</p>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-colors text-left group"
              >
                <div className="w-12 h-12 bg-blue-500/20 border border-blue-500/30 rounded-lg flex items-center justify-center">
                  <Upload className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-mono font-bold text-slate-800 dark:text-slate-100 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">UPLOAD ZIP</h3>
                  <p className="text-sm text-slate-500">
                    Import a ZIP archive with JSON and photos
                  </p>
                </div>
              </button>
              
              <button
                onClick={() => setStep('manual')}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-green-500 hover:bg-green-500/10 transition-colors text-left group"
              >
                <div className="w-12 h-12 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-500 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-mono font-bold text-slate-800 dark:text-slate-100 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors">CREATE MANUALLY</h3>
                  <p className="text-sm text-slate-500">
                    Start with an empty project
                  </p>
                </div>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Manual Form Step */}
          {step === 'manual' && (
            <form onSubmit={handleManualCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-1 tracking-wider">
                  PROJECT CODE *
                </label>
                <input
                  type="text"
                  value={manualForm.code}
                  onChange={(e) => setManualForm(f => ({ ...f, code: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="DU1048300"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-1 tracking-wider">
                  NAME / LOCATION *
                </label>
                <input
                  type="text"
                  value={manualForm.name}
                  onChange={(e) => setManualForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="NOVIGLIO"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-1 tracking-wider">
                  LINE CODE
                </label>
                <input
                  type="text"
                  value={manualForm.line}
                  onChange={(e) => setManualForm(f => ({ ...f, line: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="MV_DU10"
                />
              </div>
              
              <div>
                <label className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-1 tracking-wider">
                  INSPECTION DATE
                </label>
                <input
                  type="date"
                  value={manualForm.surveyDate}
                  onChange={(e) => setManualForm(f => ({ ...f, surveyDate: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 dark:bg-red-500/20 border border-red-500/30 text-red-500 dark:text-red-400 rounded-lg text-sm font-mono">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors font-mono"
                >
                  BACK
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors font-mono font-bold"
                >
                  CREATE
                </button>
              </div>
            </form>
          )}

          {/* Loading Step */}
          {step === 'loading' && (
            <div className="py-6">
              {/* Stage indicators */}
              <div className="flex justify-between mb-6">
                {loadingStages.map((stage, index) => {
                  const currentIndex = loadingStages.findIndex(s => s.id === loadingStage);
                  const isCompleted = index < currentIndex;
                  const isCurrent = stage.id === loadingStage;
                  
                  return (
                    <div key={stage.id} className="flex flex-col items-center flex-1">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono font-bold mb-1 transition-all
                        ${isCompleted ? 'bg-green-500/20 border border-green-500/50 text-green-500 dark:text-green-400' : ''}
                        ${isCurrent ? 'bg-blue-500/20 border border-blue-500/50 text-blue-500 dark:text-blue-400 animate-pulse' : ''}
                        ${!isCompleted && !isCurrent ? 'bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600' : ''}
                      `}>
                        {isCompleted ? <CheckCircle className="w-4 h-4" /> : index + 1}
                      </div>
                      <span className={`text-[10px] font-mono ${isCurrent ? 'text-blue-500 dark:text-blue-400' : 'text-slate-400 dark:text-slate-600'}`}>
                        {stage.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Current stage info */}
              <div className="text-center mb-4">
                <Loader2 className="w-10 h-10 text-blue-500 dark:text-blue-400 animate-spin mx-auto mb-3" />
                <p className="text-slate-800 dark:text-slate-100 font-mono font-medium">{loadingMessage}</p>
              </div>

              {/* Progress bar - only for upload stage */}
              {loadingStage === 'uploading' ? (
                <div className="space-y-2">
                  <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2">
                    <div 
                      className="h-2 bg-blue-500 rounded-full transition-all"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-slate-500 font-mono">{Math.round(loadingProgress)}%</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-blue-500 rounded-full animate-pulse w-full opacity-50" />
                  </div>
                  <p className="text-center text-sm text-slate-500 font-mono">Processing on server...</p>
                </div>
              )}
            </div>
          )}

          {/* Success Step - auto close after 1.5s */}
          {step === 'success' && (
            <SuccessStep onComplete={handleSuccess} />
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-500/20 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-mono font-bold text-slate-800 dark:text-slate-100 mb-2 tracking-wide">
                ERROR
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6 font-mono text-sm">{error}</p>
              <button
                onClick={() => setStep('choose')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors font-mono font-bold"
              >
                TRY AGAIN
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
